import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import customFetch from '@utils/customFetch';
import {
  PlusIcon,
  ArrowLeftIcon,
  CheckIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  SUBSCRIPTION_STATUS,
  COMPANY_STATUS,
  COMPANY_ROLES,
} from '@utils/constants';
import NewUserSlideIn from '../benutzerverwaltung/newUserSlideIn';
import ConfirmModal from '@components/shared/ConfirmModal';
import Pagination from '@components/dashboard/admin/shared/Pagination';

const USERS_PAGE_SIZE = 20;

const companyDetailQuery = (companyId) => ({
  queryKey: ['company', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}`);
    return data;
  },
});

// Seitenweise: der Server liefert höchstens USERS_PAGE_SIZE pro Abruf.
const companyUsersQuery = (companyId, page = 1) => ({
  queryKey: ['companyUsers', companyId, 'page', page],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}/users`, {
      params: { page, limit: USERS_PAGE_SIZE },
    });
    return data;
  },
});

const discountQuery = (companyId) => ({
  queryKey: ['discount', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}/discount`);
    return data;
  },
});

const invitationsQuery = (companyId) => ({
  queryKey: ['invitations', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}/invitations`);
    return data;
  },
});

export const loader =
  (queryClient) =>
  async ({ params }) => {
    const { id: companyId } = params;
    try {
      await Promise.all([
        queryClient.ensureQueryData(companyDetailQuery(companyId)),
        queryClient.ensureQueryData(companyUsersQuery(companyId)),
      ]);
    } catch (error) {
      console.error('Error loading company data:', error);
    }
    return { companyId };
  };

const statusLabels = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'Aktiv',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'Überfällig',
  [SUBSCRIPTION_STATUS.CANCELED]: 'Gekündigt',
  [SUBSCRIPTION_STATUS.TRIALING]: 'Testphase',
  [SUBSCRIPTION_STATUS.UNPAID]: 'Unbezahlt',
};

const statusColors = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'bg-green-50 text-green-700 ring-green-600/20',
  [SUBSCRIPTION_STATUS.PAST_DUE]:
    'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
  [SUBSCRIPTION_STATUS.CANCELED]: 'bg-red-50 text-red-700 ring-red-600/20',
  [SUBSCRIPTION_STATUS.TRIALING]: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  [SUBSCRIPTION_STATUS.UNPAID]: 'bg-red-50 text-red-700 ring-red-600/20',
};

// Felder, deren leere Auswahl "kein Wert" bedeutet und nicht "Leerstring".
// `subscriptionStatus` ist ein Enum und lehnt '' ab: ohne diese Umsetzung
// scheitert JEDES Speichern einer Firma ohne Abo mit 400 — samt aller anderen
// Änderungen im Formular, auch wenn der Status gar nicht angefasst wurde.
const NULLABLE_COMPANY_FIELDS = [
  'subscriptionStatus',
  'stripeCustomerId',
  'stripeSubscriptionId',
];

const toCompanyPayload = (formData) =>
  Object.fromEntries(
    Object.entries(formData).map(([key, value]) =>
      NULLABLE_COMPANY_FIELDS.includes(key) && value === ''
        ? [key, null]
        : [key, value]
    )
  );

const CompanyDetail = () => {
  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showAddUserSlideIn, setShowAddUserSlideIn] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [roleChangingUserId, setRoleChangingUserId] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    loading: false,
  });
  const [discountForm, setDiscountForm] = useState({
    type: 'percent',
    value: '',
    duration: 'forever',
    durationInMonths: '',
    name: '',
    note: '',
  });

  const {
    data: companyData,
    isLoading: companyLoading,
    error: companyError,
  } = useQuery(companyDetailQuery(companyId));
  const [usersPage, setUsersPage] = useState(1);
  const { data: usersData, isLoading: usersLoading } = useQuery({
    ...companyUsersQuery(companyId, usersPage),
    keepPreviousData: true,
  });
  const { data: discountData, isLoading: discountDataLoading } = useQuery(
    discountQuery(companyId)
  );
  const { data: invitationsData, isLoading: invitationsLoading } = useQuery(
    invitationsQuery(companyId)
  );

  const company = companyData?.company;
  const users = usersData?.users || [];
  const usersTotalPages = usersData?.totalPages ?? 0;
  // Nach dem Entfernen des letzten Benutzers einer Seite zurückblättern.
  useEffect(() => {
    if (usersTotalPages && usersPage > usersTotalPages) {
      setUsersPage(usersTotalPages);
    }
  }, [usersPage, usersTotalPages]);
  const discount = discountData?.discount;
  const discountNote = discountData?.note;
  const invitations = invitationsData?.invitations || [];

  useEffect(() => {
    if (company && !isEditing) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        website: company.website || '',
        status: company.status || COMPANY_STATUS.ACTIVE,
        stripeCustomerId: company.stripeCustomerId || '',
        stripeSubscriptionId: company.stripeSubscriptionId || '',
        subscriptionStatus: company.subscriptionStatus || '',
        paidUsers: company.paidUsers || 0,
      });
    }
  }, [company, isEditing]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));
  };

  const handleSaveCompany = async () => {
    try {
      await customFetch.patch(`/companies/${companyId}`, toCompanyPayload(formData));
      queryClient.invalidateQueries(['company', companyId]);
      toast.success('Firma erfolgreich aktualisiert!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Speichern');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        website: company.website || '',
        status: company.status || COMPANY_STATUS.ACTIVE,
        stripeCustomerId: company.stripeCustomerId || '',
        stripeSubscriptionId: company.stripeSubscriptionId || '',
        subscriptionStatus: company.subscriptionStatus || '',
        paidUsers: company.paidUsers || 0,
      });
    }
  };

  const handleAddUserToCompany = async (userData) => {
    const data = Object.fromEntries(userData);
    try {
      if (data.userId) {
        // Bestehenden User zur Firma hinzufügen
        await customFetch.post(`/companies/${companyId}/users/${data.userId}`);
      } else {
        // Neuen User direkt anlegen (Variante C)
        await customFetch.post(`/companies/${companyId}/users/create`, {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          sendMail: data.sendMail === 'on',
          // Nur ohne Aktivierungs-E-Mail gesetzt (siehe NewUserSlideIn)
          ...(data.password ? { password: data.password } : {}),
        });
      }
      queryClient.invalidateQueries(['companyUsers', companyId]);
      queryClient.invalidateQueries(['company', companyId]);
      toast.success(data.userId ? 'Benutzer zur Firma hinzugefügt!' : 'Benutzer erstellt!');
      setShowAddUserSlideIn(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.msg || 'Fehler beim Hinzufügen des Benutzers'
      );
    }
  };

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ open: true, title, message, onConfirm, loading: false });
  };
  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, open: false }));
  };

  const handleRemoveUser = (userId, userName) => {
    openConfirm(
      'Benutzer entfernen',
      `${userName} wirklich aus der Firma entfernen?`,
      async () => {
        setConfirmModal((prev) => ({ ...prev, loading: true }));
        try {
          await customFetch.delete(`/companies/${companyId}/users/${userId}`);
          queryClient.invalidateQueries(['companyUsers', companyId]);
          queryClient.invalidateQueries(['company', companyId]);
          toast.success('Benutzer entfernt');
        } catch (error) {
          toast.error(error?.response?.data?.msg || 'Fehler beim Entfernen');
        } finally {
          closeConfirm();
        }
      }
    );
  };

  const handleApplyDiscount = async (e) => {
    e.preventDefault();
    setDiscountLoading(true);
    try {
      const payload = {
        type: discountForm.type,
        value: parseFloat(discountForm.value),
        duration: discountForm.duration,
      };
      if (discountForm.duration === 'repeating') {
        payload.durationInMonths = parseInt(discountForm.durationInMonths);
      }
      if (discountForm.name) payload.name = discountForm.name;
      if (discountForm.note) payload.note = discountForm.note;

      await customFetch.post(`/companies/${companyId}/discount`, payload);
      queryClient.invalidateQueries(['discount', companyId]);
      toast.success('Rabatt erfolgreich angewendet');
      setShowDiscountModal(false);
      setDiscountForm({ type: 'percent', value: '', duration: 'forever', durationInMonths: '', name: '', note: '' });
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Anwenden des Rabatts');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    openConfirm(
      'Rabatt entfernen',
      'Rabatt wirklich entfernen?',
      async () => {
        setConfirmModal((prev) => ({ ...prev, loading: true }));
        try {
          await customFetch.delete(`/companies/${companyId}/discount`);
          queryClient.invalidateQueries(['discount', companyId]);
          toast.success('Rabatt entfernt');
        } catch (error) {
          toast.error(error?.response?.data?.msg || 'Fehler beim Entfernen des Rabatts');
        } finally {
          closeConfirm();
        }
      }
    );
  };

  const handleChangeRole = async (userId, newRole) => {
    setRoleChangingUserId(userId);
    try {
      await customFetch.patch(`/companies/${companyId}/users/${userId}/role`, { companyRole: newRole });
      queryClient.invalidateQueries(['companyUsers', companyId]);
      toast.success('Rolle erfolgreich geändert');
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Ändern der Rolle');
    } finally {
      setRoleChangingUserId(null);
    }
  };

  const handleCancelInvitation = (invitationId, email) => {
    openConfirm(
      'Einladung zurückziehen',
      `Einladung an ${email} wirklich zurückziehen?`,
      async () => {
        setConfirmModal((prev) => ({ ...prev, loading: true }));
        try {
          await customFetch.delete(`/companies/${companyId}/invitations/${invitationId}`);
          queryClient.invalidateQueries(['invitations', companyId]);
          toast.success('Einladung zurückgezogen');
        } catch (error) {
          toast.error(error?.response?.data?.msg || 'Fehler beim Zurückziehen der Einladung');
        } finally {
          closeConfirm();
        }
      }
    );
  };

  const handleResendInvitation = async (email) => {
    try {
      await customFetch.post(`/companies/${companyId}/users/invite`, { email });
      queryClient.invalidateQueries(['invitations', companyId]);
      toast.success(`Einladung erneut an ${email} gesendet`);
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim erneuten Senden');
    }
  };

  if (companyLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-svisit-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (companyError || !company) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600">Fehler beim Laden der Firmendaten</p>
          <button
            onClick={() => navigate('/dashboard/firmenverwaltung')}
            className="mt-4 text-svisit-primary-600 hover:text-svisit-primary-700"
          >
            Zurück zur Firmenverwaltung
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmModal
        open={confirmModal.open}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Bestätigen"
        loading={confirmModal.loading}
      />
      <NewUserSlideIn
        showSlideIn={showAddUserSlideIn}
        setShowSlideIn={setShowAddUserSlideIn}
        onSubmit={handleAddUserToCompany}
        companyContext
      />

      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <button
              onClick={() => navigate('/dashboard/firmenverwaltung')}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Zurück zur Firmenverwaltung
            </button>
            <Link
              to={`/dashboard/protokoll?company=${companyId}`}
              className="text-sm font-medium text-svisit-primary-600 hover:text-svisit-primary-900"
            >
              Im Audit-Log ansehen
            </Link>
          </div>

          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                {company.name}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {company.slug && `Slug: ${company.slug}`}
              </p>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              {isEditing ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCompany}
                    className="rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500"
                  >
                    Speichern
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Link
                    to={`/dashboard/firmenverwaltung/${companyId}/bestellung`}
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                  >
                    Bestellung erstellen
                  </Link>
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500"
                  >
                    Bearbeiten
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="mb-8">
          <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
            <div className="px-4 py-6 sm:p-8">
              <h2 className="text-base font-semibold leading-7 text-gray-900 mb-6">
                Firmeninformationen
              </h2>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Firmenname
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  ) : (
                    <p className="mt-2 text-sm text-gray-900">{company.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Firmen-Status
                  </label>
                  {isEditing ? (
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    >
                      <option value={COMPANY_STATUS.ACTIVE}>Aktiv</option>
                      <option value={COMPANY_STATUS.PENDING}>Ausstehend</option>
                      <option value={COMPANY_STATUS.SUSPENDED}>Gesperrt</option>
                    </select>
                  ) : (
                    <p className="mt-2 text-sm text-gray-900">
                      {company.status}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Abo-Status
                  </label>
                  {isEditing ? (
                    <select
                      name="subscriptionStatus"
                      value={formData.subscriptionStatus}
                      onChange={handleInputChange}
                      className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    >
                      <option value="">–</option>
                      <option value="active">Aktiv</option>
                      <option value="past_due">Überfällig</option>
                      <option value="canceled">Gekündigt</option>
                      <option value="unpaid">Unbezahlt</option>
                      <option value="trialing">Testphase</option>
                    </select>
                  ) : (
                    <div className="mt-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                          statusColors[company.subscriptionStatus] ||
                          'bg-gray-50 text-gray-700 ring-gray-600/20'
                        }`}
                      >
                        {statusLabels[company.subscriptionStatus] ||
                          company.subscriptionStatus ||
                          '–'}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    E-Mail
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  ) : (
                    <p className="mt-2 text-sm text-gray-900">
                      {company.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Website
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  ) : (
                    <p className="mt-2 text-sm text-gray-900">
                      {company.website || '–'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Stripe Kunden-ID
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="stripeCustomerId"
                      value={formData.stripeCustomerId}
                      onChange={handleInputChange}
                      className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  ) : (
                    <p className="mt-2 text-sm text-gray-900 font-mono">
                      {company.stripeCustomerId || '–'}
                    </p>
                  )}
                </div>

                {(company.stripeSubscriptionId || isEditing) && (
                  <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">
                      Stripe Subscription-ID
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="stripeSubscriptionId"
                        value={formData.stripeSubscriptionId}
                        onChange={handleInputChange}
                        className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                      />
                    ) : (
                      <p className="mt-2 text-sm text-gray-900 font-mono">
                        {company.stripeSubscriptionId}
                      </p>
                    )}
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Erstellt am
                  </label>
                  <p className="mt-2 text-sm text-gray-900">
                    {new Date(company.createdAt).toLocaleDateString('de-CH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Discount / Rabatt */}
        <div className="mb-8">
          <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
            <div className="px-4 py-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  Rabatt
                </h2>
                {discount ? (
                  <button
                    type="button"
                    onClick={handleRemoveDiscount}
                    className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-red-600 shadow-xs ring-1 ring-inset ring-red-300 hover:bg-red-50"
                  >
                    Rabatt entfernen
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowDiscountModal(true)}
                    className="rounded-md bg-svisit-primary-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500"
                  >
                    Rabatt zuweisen
                  </button>
                )}
              </div>

              {discountDataLoading ? (
                <p className="text-sm text-gray-500">Laden...</p>
              ) : discount ? (
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {discount.pending && (
                    <div className="sm:col-span-2">
                      <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                        Vorgemerkt — wird beim Abo-Abschluss auf die erste
                        Rechnung angewendet
                      </span>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Bezeichnung</dt>
                    <dd className="mt-1 text-sm text-gray-900">{discount.name || '–'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Rabatt</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {discount.percentOff
                        ? `${discount.percentOff}%`
                        : `CHF ${discount.amountOff?.toFixed(2)}`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Dauer</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {discount.duration === 'forever' && 'Permanent'}
                      {discount.duration === 'once' && 'Einmalig'}
                      {discount.duration === 'repeating' &&
                        `${discount.durationInMonths} Monate`}
                    </dd>
                  </div>
                  {discountNote && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Interne Notiz</dt>
                      <dd className="mt-1 text-sm text-gray-900">{discountNote}</dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-gray-500">
                  {company.stripeSubscriptionId
                    ? 'Kein Rabatt aktiv'
                    : 'Kein Rabatt hinterlegt. Ein jetzt zugewiesener Rabatt wird vorgemerkt und beim Abo-Abschluss automatisch angewendet.'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Discount Modal */}
        {showDiscountModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-gray-500/75" onClick={() => setShowDiscountModal(false)} />
              <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Rabatt zuweisen</h3>
                  <button onClick={() => setShowDiscountModal(false)} className="text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleApplyDiscount} className="space-y-4">
                  {/* Rabatttyp */}
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-900 mb-2">Rabatttyp</legend>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="discountType"
                          value="percent"
                          checked={discountForm.type === 'percent'}
                          onChange={() => setDiscountForm((p) => ({ ...p, type: 'percent', value: '' }))}
                          className="text-svisit-primary-600 focus:ring-svisit-primary-600"
                        />
                        Prozent
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="discountType"
                          value="fixed"
                          checked={discountForm.type === 'fixed'}
                          onChange={() => setDiscountForm((p) => ({ ...p, type: 'fixed', value: '' }))}
                          className="text-svisit-primary-600 focus:ring-svisit-primary-600"
                        />
                        Fixbetrag (CHF)
                      </label>
                    </div>
                  </fieldset>

                  {/* Wert */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900">
                      Wert {discountForm.type === 'percent' ? '(1–100%)' : '(CHF)'}
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max={discountForm.type === 'percent' ? 100 : undefined}
                      step={discountForm.type === 'fixed' ? '0.01' : '1'}
                      value={discountForm.value}
                      onChange={(e) => setDiscountForm((p) => ({ ...p, value: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm"
                    />
                  </div>

                  {/* Dauer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900">Dauer</label>
                    <select
                      value={discountForm.duration}
                      onChange={(e) => setDiscountForm((p) => ({ ...p, duration: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm"
                    >
                      <option value="once">Einmalig (nur nächste Rechnung)</option>
                      <option value="forever">Permanent (jede Rechnung)</option>
                      <option value="repeating">Zeitlich begrenzt</option>
                    </select>
                  </div>

                  {/* Monate (nur bei repeating) */}
                  {discountForm.duration === 'repeating' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-900">Anzahl Monate</label>
                      <input
                        type="number"
                        required
                        min="1"
                        max="36"
                        value={discountForm.durationInMonths}
                        onChange={(e) => setDiscountForm((p) => ({ ...p, durationInMonths: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm"
                      />
                    </div>
                  )}

                  {/* Bezeichnung */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900">
                      Bezeichnung <span className="text-gray-400">(optional, erscheint auf Stripe-Rechnung)</span>
                    </label>
                    <input
                      type="text"
                      maxLength="100"
                      value={discountForm.name}
                      onChange={(e) => setDiscountForm((p) => ({ ...p, name: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm"
                    />
                  </div>

                  {/* Interne Notiz */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900">
                      Interne Notiz <span className="text-gray-400">(optional, nur im Admin sichtbar)</span>
                    </label>
                    <textarea
                      maxLength="500"
                      rows="2"
                      value={discountForm.note}
                      onChange={(e) => setDiscountForm((p) => ({ ...p, note: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowDiscountModal(false)}
                      className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={discountLoading}
                      className="rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300"
                    >
                      {discountLoading ? 'Wird angewendet...' : 'Rabatt anwenden'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <div className="mb-8">
            <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
              <div className="px-4 py-6 sm:p-8">
                <h2 className="text-base font-semibold leading-7 text-gray-900 mb-6">
                  Offene Einladungen ({invitationsLoading ? '...' : invitations.length})
                </h2>
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        E-Mail
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Eingeladen von
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Läuft ab
                      </th>
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Aktionen</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {invitations.map((inv) => (
                      <tr key={inv._id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                          {inv.email}
                          {inv.name && (
                            <span className="ml-2 text-gray-500">({inv.name})</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {inv.expired ? (
                            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                              Abgelaufen
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                              Ausstehend
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {inv.invitedBy
                            ? `${inv.invitedBy.firstName} ${inv.invitedBy.lastName}`
                            : '–'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(inv.expiresAt).toLocaleDateString('de-CH', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex justify-end gap-3">
                            {inv.expired && (
                              <button
                                onClick={() => handleResendInvitation(inv.email)}
                                className="text-svisit-primary-600 hover:text-svisit-primary-900"
                              >
                                Erneut senden
                              </button>
                            )}
                            <button
                              onClick={() => handleCancelInvitation(inv._id, inv.email)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Zurückziehen
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Company Users */}
        <div>
          <div className="sm:flex sm:items-center sm:justify-between mb-6">
            <div>
              <h2 className="text-base font-semibold leading-7 text-gray-900">
                Firmenbenutzer ({usersLoading ? '...' : usersData?.total ?? users.length})
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Alle Benutzer, die dieser Firma zugeordnet sind
              </p>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              <button
                type="button"
                onClick={() => setShowAddUserSlideIn(true)}
                className="flex items-center rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Benutzer hinzufügen
              </button>
            </div>
          </div>

          <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Name
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      E-Mail
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Firmenrolle
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Verifiziert
                    </th>
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Aktionen</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {usersLoading ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-8 text-center text-sm text-gray-500"
                      >
                        Lade Benutzer...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-8 text-center text-sm text-gray-500"
                      >
                        Noch keine Benutzer in dieser Firma
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {user.email}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <select
                            value={user.companyRole}
                            disabled={roleChangingUserId === user._id}
                            onChange={(e) => handleChangeRole(user._id, e.target.value)}
                            className="rounded-md border-0 py-1 pl-2 pr-7 text-xs font-medium ring-1 ring-inset focus:ring-2 focus:ring-svisit-primary-600 disabled:opacity-50 disabled:cursor-wait
                              bg-white text-gray-700 ring-gray-300"
                          >
                            <option value={COMPANY_ROLES.MEMBER}>Mitglied</option>
                            <option value={COMPANY_ROLES.COMPANY_ADMIN}>Admin</option>
                          </select>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {user.status === 'active' ? (
                            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              Aktiv
                            </span>
                          ) : user.status === 'blocked' ? (
                            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                              Blockiert
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                              {user.status}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {user.isVerified ? (
                            <CheckIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <span className="text-xs text-gray-400">Nein</span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex justify-end gap-3">
                            <a
                              href={`/dashboard/benutzerverwaltung/${user._id}`}
                              className="text-svisit-primary-600 hover:text-svisit-primary-900"
                            >
                              Bearbeiten
                            </a>
                            <button
                              onClick={() =>
                                handleRemoveUser(
                                  user._id,
                                  `${user.firstName} ${user.lastName}`
                                )
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              Entfernen
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <Pagination
                currentPage={usersPage}
                numOfPages={usersTotalPages}
                total={usersData?.total ?? 0}
                limit={USERS_PAGE_SIZE}
                onPageChange={setUsersPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyDetail;
